import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Starfield } from "@/components/Starfield";
import api, { fileUrl, formatPrice } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Store,
  Package,
  Settings as SettingsIcon,
  Receipt,
  Upload,
  Moon,
  ArrowUp,
  ArrowDown,
  KeyRound,
  ListOrdered,
  TrendingUp,
  Wallet,
  Banknote,
  Trophy,
  BarChart3,
  CreditCard,
  Truck,
} from "lucide-react";
import { toast } from "sonner";

const emptyProduct = {
  name: "",
  description: "",
  category: "",
  price: "",
  image_url: "",
  in_stock: true,
};

export const AdminDashboard = () => {
  const { logout, user, applyAuth } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ store_name: "", whatsapp_number: "", currency: "€", tagline: "", category_order: [] });
  const [catOrder, setCatOrder] = useState([]);
  const [credForm, setCredForm] = useState({ email: "", current_password: "", new_password: "" });
  const [savingCred, setSavingCred] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOrderTarget, setDeleteOrderTarget] = useState(null);
  const [stats, setStats] = useState(null);
  const [zones, setZones] = useState([]);
  const [zoneForm, setZoneForm] = useState({ label: "", postal_prefix: "", city: "", fee: "0" });
  const [savingZone, setSavingZone] = useState(false);
  const fileRef = useRef();

  const loadAll = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => setSettings({ category_order: [], ...r.data }));
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
  };
  useEffect(loadAll, []);

  // Keep the editable category order in sync with products + saved order
  useEffect(() => {
    const present = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    const saved = settings.category_order || [];
    setCatOrder([
      ...saved.filter((c) => present.includes(c)),
      ...present.filter((c) => !saved.includes(c)),
    ]);
  }, [products, settings.category_order]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price: String(p.price) });
    setDialogOpen(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error("Échec du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const saveProduct = async () => {
    if (!form.name.trim() || !form.price) {
      toast.error("Nom et prix requis");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description || "",
      category: form.category || "",
      price: parseFloat(form.price),
      image_url: form.image_url || "",
      in_stock: form.in_stock,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produit modifié");
      } else {
        await api.post("/products", payload);
        toast.success("Produit ajouté");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success("Produit supprimé");
      setDeleteTarget(null);
      loadAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const confirmOrderDelete = async () => {
    try {
      await api.delete(`/orders/${deleteOrderTarget.id}`);
      toast.success("Commande supprimée");
      setDeleteOrderTarget(null);
      loadAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addZone = async () => {
    if (!zoneForm.label.trim() || !zoneForm.postal_prefix.trim()) {
      toast.error("Nom et code postal (ou préfixe) requis");
      return;
    }
    setSavingZone(true);
    try {
      await api.post("/zones", {
        label: zoneForm.label.trim(),
        postal_prefix: zoneForm.postal_prefix.trim(),
        city: zoneForm.city.trim(),
        fee: parseFloat(zoneForm.fee) || 0,
        active: true,
      });
      setZoneForm({ label: "", postal_prefix: "", city: "", fee: "0" });
      toast.success("Zone ajoutée");
      loadAll();
    } catch {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSavingZone(false);
    }
  };

  const saveZone = async (zone, changes) => {
    try {
      await api.put(`/zones/${zone.id}`, {
        label: zone.label,
        postal_prefix: zone.postal_prefix,
        city: zone.city || "",
        fee: Number(zone.fee) || 0,
        active: zone.active,
        ...changes,
      });
      loadAll();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const deleteZone = async (id) => {
    try {
      await api.delete(`/zones/${id}`);
      toast.success("Zone supprimée");
      loadAll();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", {
        store_name: settings.store_name,
        whatsapp_number: settings.whatsapp_number,
        currency: settings.currency || "€",
        tagline: settings.tagline || "",
        category_order: settings.category_order || [],
      });
      toast.success("Réglages enregistrés");
    } catch {
      toast.error("Erreur d'enregistrement");
    }
  };

  const moveProduct = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= products.length) return;
    const next = [...products];
    [next[index], next[target]] = [next[target], next[index]];
    setProducts(next);
    try {
      await api.put("/products/reorder", { ordered_ids: next.map((p) => p.id) });
    } catch {
      toast.error("Erreur lors du tri");
      loadAll();
    }
  };

  const moveCategory = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= catOrder.length) return;
    const next = [...catOrder];
    [next[index], next[target]] = [next[target], next[index]];
    setCatOrder(next);
    setSettings((s) => ({ ...s, category_order: next }));
    try {
      await api.put("/settings", {
        store_name: settings.store_name,
        whatsapp_number: settings.whatsapp_number,
        currency: settings.currency || "€",
        tagline: settings.tagline || "",
        category_order: next,
      });
    } catch {
      toast.error("Erreur lors du tri des catégories");
    }
  };

  const saveCredentials = async () => {
    if (!credForm.current_password) {
      toast.error("Entrez votre mot de passe actuel pour confirmer");
      return;
    }
    if (!credForm.email.trim() && !credForm.new_password) {
      toast.error("Renseignez un nouvel email ou un nouveau mot de passe");
      return;
    }
    setSavingCred(true);
    try {
      const { data } = await api.put("/auth/credentials", {
        current_password: credForm.current_password,
        email: credForm.email.trim() || null,
        new_password: credForm.new_password || null,
      });
      applyAuth(data);
      setCredForm({ email: "", current_password: "", new_password: "" });
      toast.success("Identifiants mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSavingCred(false);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Starfield />
      <div className="relative z-10">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full brand-terracotta flex items-center justify-center">
              <Moon className="h-5 w-5 twinkle" />
            </div>
            <div className="leading-none">
              <p className="font-heading text-lg font-bold">Espace Admin</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="view-store-link"
              className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-full hover:bg-muted"
            >
              <Store className="h-4 w-4" /> Voir la boutique
            </Link>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-border hover:bg-muted"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Tableau de bord
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              <Package className="h-4 w-4 mr-1.5" /> Produits
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">
              <Receipt className="h-4 w-4 mr-1.5" /> Commandes
            </TabsTrigger>
            <TabsTrigger value="delivery" data-testid="tab-delivery">
              <Truck className="h-4 w-4 mr-1.5" /> Livraison
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <SettingsIcon className="h-4 w-4 mr-1.5" /> Réglages
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard">
            <h2 className="font-heading text-2xl font-bold mb-5">Récapitulatif de l'activité</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="stat-revenue" className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-terracotta">
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Chiffre d'affaires</span>
                </div>
                <p className="font-heading text-3xl font-bold mt-3">{formatPrice(stats?.revenue_total || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats?.orders_total || 0} commande(s)</p>
              </div>

              <div data-testid="stat-cash" className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-terracotta">
                  <Banknote className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Ventes espèces</span>
                </div>
                <p className="font-heading text-3xl font-bold mt-3">{formatPrice(stats?.cash?.revenue || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats?.cash?.count || 0} commande(s)</p>
              </div>

              <div data-testid="stat-card" className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-terracotta">
                  <CreditCard className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Ventes carte bleue</span>
                </div>
                <p className="font-heading text-3xl font-bold mt-3">{formatPrice(stats?.card?.revenue || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats?.card?.count || 0} commande(s)</p>
              </div>

              <div data-testid="stat-avg" className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 text-terracotta">
                  <Wallet className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-wide font-semibold">Panier moyen</span>
                </div>
                <p className="font-heading text-3xl font-bold mt-3">{formatPrice(stats?.avg_basket || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">par commande</p>
              </div>
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl p-5 max-w-2xl">
              <h3 className="font-heading text-xl font-semibold flex items-center gap-2 mb-4">
                <Trophy className="h-5 w-5 text-terracotta" /> Meilleures ventes
              </h3>
              {(!stats?.top_products || stats.top_products.length === 0) ? (
                <p className="text-sm text-muted-foreground">Aucune vente enregistrée pour le moment.</p>
              ) : (
                <div className="space-y-2">
                  {stats.top_products.map((p, i) => (
                    <div key={p.name} data-testid={`top-product-${i}`} className="flex items-center justify-between border border-border rounded-xl px-3 py-2">
                      <span className="text-sm font-medium truncate">{i + 1}. {p.name}</span>
                      <span className="text-sm text-muted-foreground shrink-0 ml-2">
                        {p.quantity} vendu(s) · {formatPrice(p.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-2xl font-bold">Catalogue ({products.length})</h2>
              <button
                data-testid="add-product-button"
                onClick={openNew}
                className="brand-terracotta text-white rounded-full px-4 h-11 flex items-center gap-2 font-semibold active:scale-95 transition-transform"
              >
                <Plus className="h-5 w-5" /> Ajouter
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Utilisez les flèches pour changer l'ordre d'affichage des produits sur la boutique.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p, index) => (
                <div
                  key={p.id}
                  data-testid={`admin-product-${p.id}`}
                  className="bg-card border border-border rounded-2xl overflow-hidden flex"
                >
                  <div className="flex flex-col items-center justify-center gap-1 px-1 bg-muted/40 border-r border-border">
                    <button
                      data-testid={`move-up-${p.id}`}
                      onClick={() => moveProduct(index, -1)}
                      disabled={index === 0}
                      className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-muted disabled:opacity-30"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <span className="text-[11px] font-bold text-muted-foreground">{index + 1}</span>
                    <button
                      data-testid={`move-down-${p.id}`}
                      onClick={() => moveProduct(index, 1)}
                      disabled={index === products.length - 1}
                      className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-muted disabled:opacity-30"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="h-28 w-28 bg-muted shrink-0">
                    {p.image_url && <img src={fileUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="p-3 flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-sm leading-tight">{p.name}</p>
                      {!p.in_stock && <span className="text-[10px] text-destructive font-bold uppercase shrink-0">Rupture</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{p.category}</p>
                    <p className="text-terracotta font-bold mt-1">{formatPrice(p.price)}</p>
                    <div className="flex gap-2 mt-auto pt-2">
                      <button
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="flex-1 text-xs border border-border rounded-full py-1.5 hover:bg-muted flex items-center justify-center gap-1"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </button>
                      <button
                        data-testid={`delete-product-${p.id}`}
                        onClick={() => setDeleteTarget(p)}
                        className="text-xs border border-border rounded-full py-1.5 px-3 hover:bg-destructive hover:text-white transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders">
            <h2 className="font-heading text-2xl font-bold mb-5">Historique des commandes ({orders.length})</h2>
            {orders.length === 0 ? (
              <p className="text-muted-foreground py-10 text-center">Aucune commande pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`order-${o.id}`} className="bg-card border border-border rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold flex items-center gap-2">
                        {o.customer_name}
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${o.payment_method === "card" ? "bg-terracotta/15 text-terracotta" : "bg-muted text-muted-foreground"}`}>
                          {o.payment_method === "card" ? "Carte" : "Espèces"}
                        </span>
                      </p>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-bold text-terracotta">{formatPrice(o.total)}</span>
                        <button
                          data-testid={`delete-order-${o.id}`}
                          onClick={() => setDeleteOrderTarget(o)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          title="Supprimer la commande"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">📍 {o.delivery_address}</p>
                    <p className="text-sm mt-1">
                      {o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                    </p>
                    {o.notes && <p className="text-xs text-muted-foreground mt-1">📝 {o.notes}</p>}
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {new Date(o.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* DELIVERY ZONES */}
          <TabsContent value="delivery">
            <h2 className="font-heading text-2xl font-bold mb-2">Zones de livraison</h2>
            <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
              Seules les adresses dont le code postal commence par un préfixe actif pourront commander.
              Indiquez un préfixe (ex. <span className="text-foreground">17</span> = tout le département,
              ou <span className="text-foreground">85000</span> = une ville précise) et des frais de livraison éventuels.
            </p>

            {/* Add zone form */}
            <div className="max-w-2xl bg-card border border-border rounded-2xl p-5 mb-6">
              <h3 className="font-heading text-lg font-semibold mb-3">Ajouter une zone</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="z-label">Nom de la zone</Label>
                  <Input id="z-label" data-testid="zone-label-input" value={zoneForm.label}
                    onChange={(e) => setZoneForm({ ...zoneForm, label: e.target.value })}
                    placeholder="Ex : Charente-Maritime (17)" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="z-prefix">Code postal / préfixe</Label>
                  <Input id="z-prefix" data-testid="zone-prefix-input" value={zoneForm.postal_prefix}
                    onChange={(e) => setZoneForm({ ...zoneForm, postal_prefix: e.target.value })}
                    placeholder="17  ou  85000" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="z-city">Ville (optionnel)</Label>
                  <Input id="z-city" data-testid="zone-city-input" value={zoneForm.city}
                    onChange={(e) => setZoneForm({ ...zoneForm, city: e.target.value })}
                    placeholder="La Rochelle" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="z-fee">Frais de livraison (€)</Label>
                  <Input id="z-fee" data-testid="zone-fee-input" type="number" step="0.01" value={zoneForm.fee}
                    onChange={(e) => setZoneForm({ ...zoneForm, fee: e.target.value })}
                    placeholder="0" className="mt-1" />
                </div>
              </div>
              <button data-testid="add-zone-button" onClick={addZone} disabled={savingZone}
                className="brand-terracotta text-white rounded-full h-11 px-6 font-semibold mt-4 active:scale-95 transition-transform disabled:opacity-60">
                {savingZone ? "Ajout…" : "Ajouter la zone"}
              </button>
            </div>

            {/* Zones list */}
            <div className="max-w-2xl space-y-3">
              {zones.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune zone : la boutique livre partout.</p>
              ) : (
                zones.map((z) => (
                  <div key={z.id} data-testid={`zone-${z.id}`}
                    className="bg-card border border-border rounded-2xl p-4 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[160px]">
                      <p className="font-semibold text-sm">{z.label}</p>
                      <p className="text-xs text-muted-foreground">
                        Préfixe <span className="text-foreground font-medium">{z.postal_prefix}</span>
                        {z.city ? ` · ${z.city}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Label htmlFor={`fee-${z.id}`} className="text-xs text-muted-foreground">Frais €</Label>
                      <Input
                        id={`fee-${z.id}`}
                        data-testid={`zone-fee-${z.id}`}
                        type="number"
                        step="0.01"
                        defaultValue={z.fee}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          if (v !== Number(z.fee)) saveZone(z, { fee: v });
                        }}
                        className="h-9 w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Active</span>
                      <Switch
                        data-testid={`zone-active-${z.id}`}
                        checked={z.active}
                        onCheckedChange={(v) => saveZone(z, { active: v })}
                      />
                    </div>
                    <button
                      data-testid={`delete-zone-${z.id}`}
                      onClick={() => deleteZone(z.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      title="Supprimer la zone"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings">
            <h2 className="font-heading text-2xl font-bold mb-5">Réglages de la boutique</h2>
            <div className="max-w-lg space-y-4 bg-card border border-border rounded-2xl p-6">
              <div>
                <Label htmlFor="store-name">Nom de la boutique</Label>
                <Input
                  id="store-name"
                  data-testid="settings-store-name"
                  value={settings.store_name || ""}
                  onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="tagline">Slogan</Label>
                <Input
                  id="tagline"
                  data-testid="settings-tagline"
                  value={settings.tagline || ""}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="wa-number">Numéro WhatsApp (avec indicatif pays, sans +)</Label>
                <Input
                  id="wa-number"
                  data-testid="settings-whatsapp-number"
                  value={settings.whatsapp_number || ""}
                  onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                  placeholder="33612345678"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Exemple France : 33612345678 (33 = France, puis le numéro sans le 0)
                </p>
              </div>
              <button
                data-testid="save-settings-button"
                onClick={saveSettings}
                className="brand-terracotta text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform"
              >
                Enregistrer
              </button>
            </div>

            {/* Category order */}
            <div className="max-w-lg space-y-3 bg-card border border-border rounded-2xl p-6 mt-6">
              <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
                <ListOrdered className="h-5 w-5 text-terracotta" /> Ordre des catégories
              </h3>
              <p className="text-sm text-muted-foreground">
                Classez l'ordre des filtres de catégories affichés sur la boutique.
              </p>
              {catOrder.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune catégorie pour le moment.</p>
              ) : (
                <div className="space-y-2">
                  {catOrder.map((c, index) => (
                    <div
                      key={c}
                      data-testid={`category-row-${c}`}
                      className="flex items-center justify-between border border-border rounded-xl px-3 py-2"
                    >
                      <span className="font-medium text-sm">{index + 1}. {c}</span>
                      <div className="flex gap-1">
                        <button
                          data-testid={`cat-up-${c}`}
                          onClick={() => moveCategory(index, -1)}
                          disabled={index === 0}
                          className="h-8 w-8 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-30"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`cat-down-${c}`}
                          onClick={() => moveCategory(index, 1)}
                          disabled={index === catOrder.length - 1}
                          className="h-8 w-8 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-30"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin account */}
            <div className="max-w-lg space-y-4 bg-card border border-border rounded-2xl p-6 mt-6">
              <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-terracotta" /> Compte admin
              </h3>
              <p className="text-sm text-muted-foreground">
                Connecté en tant que <span className="font-medium text-foreground">{user?.email}</span>. Laissez un champ vide pour ne pas le modifier.
              </p>
              <div>
                <Label htmlFor="new-email">Nouvel email</Label>
                <Input
                  id="new-email"
                  data-testid="settings-new-email"
                  type="email"
                  value={credForm.email}
                  onChange={(e) => setCredForm({ ...credForm, email: e.target.value })}
                  placeholder={user?.email}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-password">Nouveau mot de passe</Label>
                <Input
                  id="new-password"
                  data-testid="settings-new-password"
                  type="password"
                  value={credForm.new_password}
                  onChange={(e) => setCredForm({ ...credForm, new_password: e.target.value })}
                  placeholder="Au moins 6 caractères"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="current-password">Mot de passe actuel (obligatoire)</Label>
                <Input
                  id="current-password"
                  data-testid="settings-current-password"
                  type="password"
                  value={credForm.current_password}
                  onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })}
                  placeholder="Confirmez avec votre mot de passe actuel"
                  className="mt-1"
                />
              </div>
              <button
                data-testid="save-credentials-button"
                onClick={saveCredentials}
                disabled={savingCred}
                className="brand-terracotta text-white rounded-full h-11 px-6 font-semibold active:scale-95 transition-transform disabled:opacity-60"
              >
                {savingCred ? "Enregistrement…" : "Mettre à jour les identifiants"}
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Modifier le produit" : "Nouveau produit"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="aspect-video rounded-xl border-2 border-dashed border-border bg-muted/50 flex items-center justify-center cursor-pointer overflow-hidden relative hover:border-terracotta transition-colors"
              data-testid="upload-dropzone"
            >
              {form.image_url ? (
                <img src={fileUrl(form.image_url)} alt="aperçu" className="h-full w-full object-cover" />
              ) : (
                <div className="text-center text-muted-foreground">
                  <Upload className="h-6 w-6 mx-auto mb-1" />
                  <p className="text-sm">{uploading ? "Téléchargement…" : "Ajouter une photo"}</p>
                </div>
              )}
              <input
                ref={fileRef}
                data-testid="product-image-input"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
            </div>
            <div>
              <Label htmlFor="p-name">Nom du produit *</Label>
              <Input
                id="p-name"
                data-testid="product-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="p-desc">Description</Label>
              <Textarea
                id="p-desc"
                data-testid="product-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 resize-none"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="p-cat">Catégorie</Label>
                <Input
                  id="p-cat"
                  data-testid="product-category-input"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="p-price">Prix (€) *</Label>
                <Input
                  id="p-price"
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="p-stock">Disponible (en stock)</Label>
              <Switch
                id="p-stock"
                data-testid="product-instock-switch"
                checked={form.in_stock}
                onCheckedChange={(v) => setForm({ ...form, in_stock: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="save-product-button"
              onClick={saveProduct}
              disabled={saving || uploading}
              className="brand-terracotta text-white rounded-full h-11 px-6 font-semibold w-full active:scale-95 transition-transform disabled:opacity-60"
            >
              {saving ? "Enregistrement…" : editing ? "Enregistrer les modifications" : "Ajouter le produit"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {deleteTarget?.name} » sera retiré du catalogue. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete order confirm */}
      <AlertDialog open={!!deleteOrderTarget} onOpenChange={(v) => !v && setDeleteOrderTarget(null)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette commande ?</AlertDialogTitle>
            <AlertDialogDescription>
              La commande de « {deleteOrderTarget?.customer_name} » ({formatPrice(deleteOrderTarget?.total || 0)}) sera supprimée de l'historique et ne comptera plus dans le chiffre d'affaires. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-order-delete-button">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-order-delete-button"
              onClick={confirmOrderDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};
